import { watch, type Ref } from "vue";
import type { GeoJSONSource, Map, MapMouseEvent } from "mapbox-gl";
import type { FeatureCollection } from "geojson";
import {
  bindPointerCursor,
  EMPTY_FEATURE_COLLECTION,
  MAP_POINT_COLOR,
  POINT_HIT_STROKE,
  removeLayersIfPresent,
  removeSourcesIfPresent,
  useAppMapStyleLayers,
} from "./useAppMapStyleLayers";

const SOURCE_ID = "sites-markers";
const LAYER_POINTS = "sites-markers-points";

interface UseAppMapMarkersOptions {
  data: Ref<FeatureCollection | undefined>;
  activeFeatureId?: Ref<number | string | undefined>;
  onFeatureClick?: (payload: { feature: GeoJSON.Feature; event: MapMouseEvent }) => void;
}

export function useAppMapMarkers(map: Ref<Map | undefined>, options: UseAppMapMarkersOptions) {
  function applyActiveFilter(m: Map) {
    if (!m.getLayer(LAYER_POINTS)) return;
    const id = options.activeFeatureId?.value;
    if (id != null) {
      m.setFilter(LAYER_POINTS, ["!=", ["to-string", ["get", "id"]], String(id)]);
    } else {
      m.setFilter(LAYER_POINTS, null);
    }
  }

  function setup(m: Map) {
    m.addSource(SOURCE_ID, {
      type: "geojson",
      data: options.data.value ?? EMPTY_FEATURE_COLLECTION,
    });

    m.addLayer({
      id: LAYER_POINTS,
      type: "circle",
      source: SOURCE_ID,
      paint: {
        "circle-color": MAP_POINT_COLOR,
        "circle-radius": 10,
        "circle-stroke-width": POINT_HIT_STROKE,
        "circle-stroke-opacity": 0,
      },
    });

    applyActiveFilter(m);

    const onPointClick = (e: MapMouseEvent) => {
      const feature = m.queryRenderedFeatures(e.point, { layers: [LAYER_POINTS] })[0];
      if (!feature) return;
      options.onFeatureClick?.({ feature: feature as GeoJSON.Feature, event: e });
    };

    const unbindCursor = bindPointerCursor(m, LAYER_POINTS);
    m.on("click", LAYER_POINTS, onPointClick);

    return () => {
      unbindCursor();
      m.off("click", LAYER_POINTS, onPointClick);
      removeLayersIfPresent(m, LAYER_POINTS);
      removeSourcesIfPresent(m, SOURCE_ID);
    };
  }

  useAppMapStyleLayers(map, setup);

  watch(options.data, (newData) => {
    const source = map.value?.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (source && newData) {
      source.setData(newData);
    }
  });

  if (options.activeFeatureId) {
    watch(options.activeFeatureId, () => {
      const m = map.value;
      if (m) applyActiveFilter(m);
    });
  }
}
